// profile.ts

import { apiRequest } from "./apiUtils";
import { 
  UpdateInfluencerProfileDto, 
  UpdateInfluencerProfileResponseDto, 
  GetPersonalInfoResponseDto,
  UpdateLocationsDto,
  RequestInstagramHandleChangeResponseDto,
  ConfirmInstagramHandleChangeResponseDto,
  RequestEmailChangeResponseDto,
  SyncEmailChangeResponseDto,
  ClaimEmailChangeResponseDto,
} from "@foodfluence/shared";

export const influencerApi = {
  updateProfile: async (
    params: UpdateInfluencerProfileDto,
    influencerId: string
  ): Promise<UpdateInfluencerProfileResponseDto> => {
    return await apiRequest({
      method: "POST",
      endpoint: `/influencer/profile/update`,
      data: params,
      influencerId,
    });
  },

  updateLocations: async (params: UpdateLocationsDto): Promise<void> => {
    await apiRequest({
      method: 'POST',
      endpoint: '/influencer/update-locations',
      data: params
    });
  },

  // Instagram handle and email are not part of updateProfile on purpose — they only take
  // effect once the creator has proven the new handle by DM or the new address by link.

  requestInstagramHandleChange: async (
    newHandle: string,
    influencerId: string
  ): Promise<RequestInstagramHandleChangeResponseDto> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/instagram-handle/request",
      data: { newHandle },
      influencerId,
    });
  },

  confirmInstagramHandleChange: async (
    influencerId: string
  ): Promise<ConfirmInstagramHandleChangeResponseDto> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/instagram-handle/confirm",
      data: {},
      influencerId,
    });
  },

  cancelInstagramHandleChange: async (influencerId: string): Promise<{ success: boolean }> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/instagram-handle/cancel",
      data: {},
      influencerId,
    });
  },

  requestEmailChange: async (
    newEmail: string,
    influencerId: string
  ): Promise<RequestEmailChangeResponseDto> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/email/request",
      data: { newEmail },
      influencerId,
    });
  },

  syncEmailChange: async (influencerId: string): Promise<SyncEmailChangeResponseDto> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/email/sync",
      data: {},
      influencerId,
    });
  },

  /**
   * Stops the change being shown. The record stays: a sent link cannot be revoked, so the change
   * has to stay reconcilable in case the creator opens it later.
   */
  cancelEmailChange: async (influencerId: string): Promise<{ success: boolean }> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/email/cancel",
      data: {},
      influencerId,
    });
  },

  /**
   * Recovery for a session that matches neither the committed address nor a phone number, which
   * is what an in-flight email change leaves behind. Sends no influencer id — the server resolves
   * the creator from the verified token claim.
   */
  claimEmailChange: async (): Promise<ClaimEmailChangeResponseDto> => {
    return await apiRequest({
      method: "POST",
      endpoint: "/influencer/email/claim",
      data: {},
    });
  },

  getPersonalInfo: async (influencerId: string): Promise<GetPersonalInfoResponseDto> => {
    return await apiRequest({
      method: 'GET',
      endpoint: '/influencer/personal-info',
      influencerId,
    });
  },
}